import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { peopleApi } from "../api/people";
import { reportsApi, type ReportData } from "../api/reports";
import { useSession } from "../hooks/useSession";

export default function Reports() {
  const qc = useQueryClient();
  const { data: me } = useSession();
  const isAdmin = me?.role === "ADMIN";

  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const targetUserId = selectedUserId ?? me?.id ?? null;

  const people = useQuery({
    queryKey: ["people"],
    queryFn: () => peopleApi.list(),
    enabled: isAdmin,
  });

  const report = useQuery({
    queryKey: ["report", targetUserId],
    queryFn: () =>
      targetUserId ? reportsApi.userReport(targetUserId) : reportsApi.myLatest(),
    retry: false,
    enabled: targetUserId !== null,
  });

  const generate = useMutation({
    mutationFn: () =>
      selectedUserId
        ? reportsApi.generateForUser(selectedUserId)
        : reportsApi.generateMyWeekly(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["report"] }),
  });

  const usersWithConvos = (people.data ?? []).filter(
    (p) => p.conversation_count > 0,
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Weekly Report</h2>
          <p className="text-sm text-slate-500">
            AI-powered analysis of Claude usage with coaching recommendations.
          </p>
        </div>
        <button
          onClick={() => generate.mutate()}
          disabled={generate.isPending}
          className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {generate.isPending ? "Generating…" : selectedUserId ? "Generate Report" : "Generate My Report"}
        </button>
      </div>

      {isAdmin && usersWithConvos.length > 0 && (
        <div className="flex items-center gap-3">
          <label className="text-sm text-slate-600">View report for:</label>
          <select
            value={selectedUserId ?? ""}
            onChange={(e) => setSelectedUserId(e.target.value ? Number(e.target.value) : null)}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm"
          >
            <option value="">Me ({me?.email})</option>
            {usersWithConvos.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.email}) — {p.conversation_count} chats
              </option>
            ))}
          </select>
        </div>
      )}

      {report.isLoading && <div className="text-sm text-slate-500">Loading…</div>}
      {report.isError && (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
          <p className="text-slate-500">No report available yet. Click "Generate Report" to create one.</p>
        </div>
      )}
      {report.data && <ReportView data={report.data.data} period={`${report.data.period_start} → ${report.data.period_end}`} generated={report.data.generated_at} />}
    </div>
  );
}

const QUALITY_COLORS = ["", "bg-rose-500", "bg-orange-500", "bg-amber-500", "bg-blue-500", "bg-emerald-500"];
const OUTCOME_ICONS: Record<string, string> = { resolved: "✓", partial: "◐", abandoned: "✗", unclear: "?" };
const OUTCOME_COLORS: Record<string, string> = { resolved: "text-emerald-700 bg-emerald-50", partial: "text-amber-700 bg-amber-50", abandoned: "text-rose-700 bg-rose-50", unclear: "text-slate-500 bg-slate-50" };

function ReportView({ data, period, generated }: { data: ReportData; period: string; generated: string }) {
  const [expandedConv, setExpandedConv] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      {data.user && (
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="mb-1 text-sm font-medium text-slate-900">
            {data.user.name} <span className="font-normal text-slate-500">({data.user.email})</span>
          </div>
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>Period: {period}</span>
            <span>Generated {new Date(generated).toLocaleString()}</span>
          </div>
          {data.summary && (
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <MiniStat label="Conversations" value={data.summary.conversation_count} />
              <MiniStat label="Total Turns" value={data.summary.total_turns} />
              <MiniStat label="Avg Turns/Conv" value={data.summary.avg_turns_per_conv} />
              <MiniStat label="Prompt Quality" value={`${data.summary.avg_prompt_quality}/5`} />
            </div>
          )}
        </div>
      )}

      {/* Quality Distribution + Outcomes side by side */}
      {(data.quality_distribution || data.outcomes) && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {data.quality_distribution && (
            <div className="rounded-lg border border-slate-200 bg-white p-6">
              <h3 className="mb-3 text-sm font-semibold text-slate-900">Quality Distribution</h3>
              <div className="flex items-end gap-2 h-24">
                {[1, 2, 3, 4, 5].map((q) => {
                  const count = (data.quality_distribution as Record<string, number>)?.[String(q)] ?? 0;
                  const max = Math.max(...Object.values(data.quality_distribution as Record<string, number>), 1);
                  return (
                    <div key={q} className="flex flex-1 flex-col items-center gap-1">
                      <div className="text-xs text-slate-500">{count}</div>
                      <div className={`w-full rounded-t ${QUALITY_COLORS[q]}`} style={{ height: `${(count / max) * 64}px`, minHeight: count > 0 ? "8px" : "2px" }} />
                      <div className="text-xs font-medium text-slate-600">{q}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {data.outcomes && (
            <div className="rounded-lg border border-slate-200 bg-white p-6">
              <h3 className="mb-3 text-sm font-semibold text-slate-900">Conversation Outcomes</h3>
              <div className="space-y-2">
                {(["resolved", "partial", "abandoned", "unclear"] as const).map((o) => {
                  const count = (data.outcomes as Record<string, number>)?.[o] ?? 0;
                  if (count === 0) return null;
                  return (
                    <div key={o} className="flex items-center gap-2">
                      <span className={`rounded px-2 py-0.5 text-xs font-medium ${OUTCOME_COLORS[o]}`}>{OUTCOME_ICONS[o]} {o}</span>
                      <span className="text-sm text-slate-700">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {data.use_case_breakdown && data.use_case_breakdown.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <h3 className="mb-3 text-sm font-semibold text-slate-900">How Claude was used</h3>
          <div className="space-y-2">
            {data.use_case_breakdown.map((uc) => (
              <div key={uc.use_case} className="flex items-center gap-3">
                <div className="w-24 text-sm text-slate-700">{uc.use_case}</div>
                <div className="flex-1">
                  <div className="h-5 rounded bg-slate-100">
                    <div className={`h-5 rounded px-2 text-xs leading-5 text-white ${uc.use_case === "personal" ? "bg-amber-500" : "bg-blue-500"}`} style={{ width: `${Math.max(uc.pct, 8)}%` }}>
                      {uc.pct}%
                    </div>
                  </div>
                </div>
                <div className="w-8 text-right text-xs text-slate-500">{uc.count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.top_topics && data.top_topics.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <h3 className="mb-3 text-sm font-semibold text-slate-900">Top Topics</h3>
          <div className="flex flex-wrap gap-2">
            {data.top_topics.map((t) => (
              <span key={t} className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700">{t}</span>
            ))}
          </div>
        </div>
      )}

      {/* Strengths */}
      {data.strengths_summary && data.strengths_summary.length > 0 && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-6">
          <h3 className="mb-3 text-sm font-semibold text-emerald-900">What's going well</h3>
          <ul className="list-disc space-y-1 pl-5 text-sm text-emerald-800">
            {data.strengths_summary.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )}

      {/* Top improvements */}
      {data.top_improvements && data.top_improvements.length > 0 && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-6">
          <h3 className="mb-3 text-sm font-semibold text-blue-900">Top areas to improve</h3>
          <ol className="list-decimal space-y-2 pl-5 text-sm text-blue-800">
            {data.top_improvements.map((h, i) => <li key={i}>{h}</li>)}
          </ol>
        </div>
      )}

      {/* Coaching hints */}
      {data.coaching_hints && data.coaching_hints.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <h3 className="mb-3 text-sm font-semibold text-slate-900">General coaching tips</h3>
          <ol className="list-decimal space-y-2 pl-5 text-sm text-slate-700">
            {data.coaching_hints.map((h, i) => <li key={i}>{h}</li>)}
          </ol>
        </div>
      )}

      {/* Per-conversation detail */}
      {data.conversations && data.conversations.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-6 py-4">
            <h3 className="text-sm font-semibold text-slate-900">Conversation-by-conversation analysis</h3>
          </div>
          <div className="divide-y divide-slate-100">
            {data.conversations.map((c) => (
              <div key={c.conversation_id}>
                <div
                  className="flex cursor-pointer items-center gap-3 px-6 py-3 hover:bg-slate-50"
                  onClick={() => setExpandedConv(expandedConv === c.conversation_id ? null : c.conversation_id)}
                >
                  <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white ${QUALITY_COLORS[c.quality]}`}>
                    {c.quality}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-slate-800">{c.title}</div>
                    <div className="truncate text-xs text-slate-500">{c.summary}</div>
                  </div>
                  <span className={`shrink-0 rounded px-2 py-0.5 text-xs ${OUTCOME_COLORS[c.outcome] ?? ""}`}>
                    {c.outcome}
                  </span>
                  <span className="shrink-0 text-xs text-slate-400">{c.turn_count} turns</span>
                  <span className="shrink-0 text-xs text-slate-400">{expandedConv === c.conversation_id ? "▲" : "▼"}</span>
                </div>
                {expandedConv === c.conversation_id && (
                  <div className="bg-slate-50 px-6 py-4 space-y-3">
                    <div className="flex flex-wrap gap-2 text-xs">
                      <span className="rounded bg-slate-200 px-2 py-0.5">{c.use_case}</span>
                      <span className="rounded bg-slate-200 px-2 py-0.5">{c.complexity}</span>
                      {c.tool_usage !== "none" && <span className="rounded bg-violet-100 text-violet-700 px-2 py-0.5">tools: {c.tool_usage}</span>}
                      {c.is_personal && <span className="rounded bg-amber-100 text-amber-700 px-2 py-0.5">personal use</span>}
                    </div>
                    {c.strengths && c.strengths.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-emerald-700 mb-1">Strengths</div>
                        <ul className="list-disc pl-5 text-xs text-emerald-800 space-y-1">
                          {c.strengths.map((s: string, i: number) => <li key={i}>{s}</li>)}
                        </ul>
                      </div>
                    )}
                    {c.improvements && c.improvements.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-blue-700 mb-1">Improvements</div>
                        <ul className="list-disc pl-5 text-xs text-blue-800 space-y-1">
                          {c.improvements.map((s: string, i: number) => <li key={i}>{s}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {data.good_prompt_example && (
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <h3 className="mb-3 text-sm font-semibold text-slate-900">Best prompt this period</h3>
          <blockquote className="border-l-4 border-emerald-300 bg-emerald-50 p-4 text-sm italic text-slate-700">
            {data.good_prompt_example.snippet}
          </blockquote>
          <p className="mt-2 text-xs text-slate-500">Quality score: {data.good_prompt_example.quality_score}/5</p>
        </div>
      )}

      {data.personal_use && data.personal_use.policy_reminder_shown && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-6">
          <h3 className="mb-2 text-sm font-semibold text-amber-900">Company AI usage reminder</h3>
          <p className="text-sm text-amber-800">
            {data.personal_use.count} conversation{data.personal_use.count !== 1 ? "s" : ""} ({data.personal_use.pct}%) classified as personal use.
            Company Claude accounts are for work — please use a personal account for personal matters.
          </p>
          {data.personal_use.pct > 25 && (
            <p className="mt-2 text-sm font-medium text-amber-900">
              Manager may follow up — consider a separate personal account.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="text-center">
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}
